/**
 * Helpers for calendar writer / rider rich-text blocks.
 */
import {
  normalizeEmail,
  resolveMemberLabel,
  tourPartyMemberBlockId,
} from './tourPartyMemberUtils';

export function escapeHtmlAttr(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

export function buildWriterLinkButtonHtml(url, label) {
  const href = escapeHtmlAttr(url);
  const text = escapeHtmlAttr(label || url || 'Open link');
  return `<a href="${href}" class="vrc-writer-link-btn" target="_blank" rel="noopener noreferrer" contenteditable="false">${text}</a><p><br/></p>`;
}

/**
 * @param {{ id?: string, name?: string, email?: string, photoURL?: string, role?: string, userTypeLabel?: string, userTypeColor?: string }} member
 * @returns {string} HTML block inserted into the writer
 */
export function buildTourPartyMemberBlock(member) {
  const name = resolveMemberLabel(member, 'Tour member');
  const email = normalizeEmail(member?.email);
  const photo = String(member?.photoURL || '').trim();
  const id = tourPartyMemberBlockId(member);
  const role = String(member?.role || '').trim();
  const userTypeLabel = String(member?.userTypeLabel || '').trim();
  const userTypeColor = String(member?.userTypeColor || '#0bb5df').trim();

  const photoHtml = photo
    ? `<img src="${escapeHtmlAttr(photo)}" alt="" class="vrc-writer-member-photo" />`
    : `<span class="vrc-writer-member-photo vrc-writer-member-photo-fallback">${escapeHtmlAttr(
        name[0] || '?'
      )}</span>`;

  const userTypeHtml = userTypeLabel
    ? `<span class="vrc-writer-member-type" style="border-color:${escapeHtmlAttr(
        userTypeColor
      )};color:${escapeHtmlAttr(userTypeColor)};">${escapeHtmlAttr(userTypeLabel)}</span>`
    : role
      ? `<span class="vrc-writer-member-role">${escapeHtmlAttr(role)}</span>`
      : '';

  return `<div class="vrc-writer-member vrc-member-card-shell" data-member-id="${escapeHtmlAttr(id)}" contenteditable="false" draggable="false">
  <div class="vrc-member-card-glimmer" aria-hidden="true"></div>
  <div class="vrc-writer-member-controls">
    <button type="button" class="vrc-writer-member-drag" draggable="true" title="Drag to move" aria-label="Drag member card">⋮⋮</button>
    <button type="button" class="vrc-writer-member-remove" title="Remove" aria-label="Remove member card">×</button>
  </div>
  <div class="vrc-writer-member-body">
    <div class="vrc-writer-member-header">
      ${photoHtml}
      <div class="vrc-writer-member-info">
        <div class="vrc-writer-member-name">${escapeHtmlAttr(name)}</div>
        ${email ? `<div class="vrc-writer-member-email">${escapeHtmlAttr(email)}</div>` : ''}
        <div class="vrc-writer-member-meta">${userTypeHtml}</div>
      </div>
    </div>
    <div class="vrc-writer-member-notes" contenteditable="true"><p>Dietary / rooming / notes…</p></div>
  </div>
</div><p><br/></p>`;
}

/** @param {string} html */
export function parseWriterMemberBlocks(html) {
  if (typeof document === 'undefined') return [];
  const el = document.createElement('div');
  el.innerHTML = String(html || '');
  return Array.from(el.querySelectorAll('.vrc-writer-member'))
    .map((node) => ({
      id: String(node.getAttribute('data-member-id') || '').trim(),
      name: String(node.querySelector('.vrc-writer-member-name')?.textContent || '').trim(),
      email: normalizeEmail(node.querySelector('.vrc-writer-member-email')?.textContent || ''),
    }))
    .filter((row) => row.id);
}

/** @param {HTMLElement} editorEl */
export function writerMemberBlockExists(editorEl, member) {
  if (!editorEl || !member) return false;
  const blockId = tourPartyMemberBlockId(member);
  const uid = String(member.id || '').trim();
  const email = normalizeEmail(member.email);
  const blocks = editorEl.querySelectorAll('.vrc-writer-member');
  for (const block of blocks) {
    const existingId = String(block.getAttribute('data-member-id') || '').trim();
    if (existingId && (existingId === blockId || (uid && existingId === uid))) return true;
    const existingEmail = normalizeEmail(block.querySelector('.vrc-writer-member-email')?.textContent || '');
    if (email && existingEmail === email) return true;
  }
  return false;
}

/** Append a member card at the end of the editor (avoids nesting inside note fields). */
export function appendTourPartyMemberBlock(editorEl, member) {
  if (!editorEl || typeof document === 'undefined') return false;
  const html = buildTourPartyMemberBlock(member);
  const temp = document.createElement('div');
  temp.innerHTML = html;
  while (temp.firstChild) {
    editorEl.appendChild(temp.firstChild);
  }
  return true;
}

/** Remove member blocks matching id, uid, or email. */
export function removeTourPartyMemberBlocks(editorEl, member) {
  if (!editorEl || !member) return;
  const blockId = tourPartyMemberBlockId(member);
  const uid = String(member.id || '').trim();
  const email = normalizeEmail(member.email);
  editorEl.querySelectorAll('.vrc-writer-member').forEach((block) => {
    const existingId = String(block.getAttribute('data-member-id') || '').trim();
    const existingEmail = normalizeEmail(block.querySelector('.vrc-writer-member-email')?.textContent || '');
    if (
      (blockId && existingId === blockId) ||
      (uid && existingId === uid) ||
      (email && (existingEmail === email || normalizeEmail(existingId) === email))
    ) {
      block.remove();
    }
  });
}

/** Strip HTML to plain text for previews / search. */
export function writerHtmlToPlainText(html) {
  if (typeof document === 'undefined') {
    return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const el = document.createElement('div');
  el.innerHTML = String(html || '');
  return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
}

/** @param {string} html */
export function isWriterHtmlEmpty(html) {
  return !writerHtmlToPlainText(html);
}

/** Prepare rider HTML for email / print (remove editor-only controls). */
export function sanitizeRiderHtmlForExport(html) {
  if (typeof document === 'undefined') return String(html || '');
  const el = document.createElement('div');
  el.innerHTML = String(html || '');
  el.querySelectorAll('.vrc-writer-member-controls, .vrc-member-card-glimmer').forEach((node) => node.remove());
  el.querySelectorAll('script, iframe, object, embed').forEach((node) => node.remove());
  el.querySelectorAll('[contenteditable]').forEach((node) => node.removeAttribute('contenteditable'));
  el.querySelectorAll('.vrc-writer-member').forEach((node) => node.removeAttribute('draggable'));
  return el.innerHTML;
}
